import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';

const ACTION_LABELS = {
  'user.login':          'Login',
  'user.logout':         'Logout',
  'user.register':       'Registrierung',
  'recipe.create':       'Rezept erstellt',
  'recipe.update':       'Rezept bearbeitet',
  'recipe.delete':       'Rezept gelöscht',
  'recipe.share_create': 'Share-Link erstellt',
  'recipe.share_revoke': 'Share-Link entfernt',
  'admin.user_delete':   'User gelöscht (Admin)',
  'admin.recipe_delete': 'Rezept gelöscht (Admin)',
  'admin.module_toggle': 'Modul umgeschaltet',
};

const ACTION_COLORS = {
  'user.login':          'var(--green)',
  'user.logout':         'var(--text-muted)',
  'user.register':       'var(--blue)',
  'recipe.create':       'var(--green)',
  'recipe.update':       'var(--amber)',
  'recipe.delete':       'var(--red)',
  'recipe.share_create': 'var(--blue)',
  'recipe.share_revoke': 'var(--text-muted)',
  'admin.user_delete':   'var(--red)',
  'admin.recipe_delete': 'var(--red)',
  'admin.module_toggle': 'var(--amber)',
};

const PAGE_SIZE = 100;

export default function AuditLog() {
  const [entries, setEntries]   = useState([]);
  const [total, setTotal]       = useState(0);
  const [offset, setOffset]     = useState(0);
  const [loading, setLoading]   = useState(false);
  const [filterUser, setFilterUser]     = useState('');
  const [filterAction, setFilterAction] = useState('');

  const load = useCallback(async (off = 0) => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: off };
      if (filterUser.trim())   params.user_id = parseInt(filterUser.trim()) || undefined;
      if (filterAction.trim()) params.action  = filterAction.trim();
      const data = await api.getAuditLog(params);
      setEntries(data.entries || []);
      setTotal(data.total || 0);
      setOffset(off);
    } catch (e) {
      console.error('AuditLog laden fehlgeschlagen:', e);
    } finally {
      setLoading(false);
    }
  }, [filterUser, filterAction]);

  useEffect(() => { load(0); }, []); // eslint-disable-line

  function formatDate(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }

  return (
    <div>
      <h1>Audit-Log</h1>
      <p className="sub">Sicherheitsrelevante Aktionen aller Benutzer</p>

      {/* Filter */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label className="admin-label">Aktion</label>
          <select className="admin-input" value={filterAction} onChange={e => setFilterAction(e.target.value)}>
            <option value="">– Alle –</option>
            {Object.entries(ACTION_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="admin-label">User-ID</label>
          <input className="admin-input" type="number" placeholder="z.B. 3"
            value={filterUser} onChange={e => setFilterUser(e.target.value)}
            style={{ width: '100px' }} />
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => load(0)} disabled={loading}>
          {loading ? 'Lädt…' : 'Filtern'}
        </button>
        {(filterUser || filterAction) && (
          <button className="btn btn-secondary btn-sm" onClick={() => { setFilterUser(''); setFilterAction(''); setTimeout(() => load(0), 0); }}>
            Zurücksetzen
          </button>
        )}
        <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
          {total} Einträge gesamt
        </span>
      </div>

      {/* Tabelle */}
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Zeitpunkt</th>
              <th>Benutzer</th>
              <th>Aktion</th>
              <th>Ressource</th>
              <th>IP-Adresse</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && !loading && (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '2rem' }}>Keine Einträge</td></tr>
            )}
            {entries.map(e => (
              <tr key={e.id}>
                <td style={{ whiteSpace: 'nowrap', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                  {formatDate(e.created_at)}
                </td>
                <td>
                  {e.username
                    ? <span style={{ fontWeight: 500 }}>{e.username}</span>
                    : <span style={{ color: 'var(--text-muted)' }}>#{e.user_id ?? '—'}</span>
                  }
                </td>
                <td>
                  <span style={{
                    display: 'inline-block',
                    padding: '0.15rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    background: (ACTION_COLORS[e.action] || 'var(--text-muted)') + '22',
                    color: ACTION_COLORS[e.action] || 'var(--text-muted)',
                  }}>
                    {ACTION_LABELS[e.action] || e.action}
                  </span>
                </td>
                <td style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                  {e.resource_type && e.resource_id
                    ? `${e.resource_type} #${e.resource_id}`
                    : e.resource_type || '—'
                  }
                </td>
                <td style={{ fontSize: '0.8rem', fontFamily: 'monospace', color: 'var(--text-muted)' }}>
                  {e.ip_address || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', alignItems: 'center' }}>
          <button className="btn btn-secondary btn-sm" disabled={offset === 0 || loading}
            onClick={() => load(Math.max(0, offset - PAGE_SIZE))}>
            ← Zurück
          </button>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} von {total}
          </span>
          <button className="btn btn-secondary btn-sm" disabled={offset + PAGE_SIZE >= total || loading}
            onClick={() => load(offset + PAGE_SIZE)}>
            Weiter →
          </button>
        </div>
      )}
    </div>
  );
}
